import { useMemo, useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ShieldCheck } from "lucide-react";
import { ATT_BLUE, ATT_CATALOG, ATT_PLAN_DETAILS, ATT_INSURANCE, ATT_TRADE_PROMO_TIERS, ATT_NEXT_UP, findDevice, parseMoney, storagePrice } from "@/attData";

const fmt = (n) => "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StepLabel = ({ n, children }) => (
  <div className="flex items-center gap-3">
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full font-body text-xs font-bold text-white" style={{ backgroundColor: ATT_BLUE }}>{n}</span>
    <h3 className="font-serif-display text-xl md:text-2xl font-bold text-[#1d2329]">{children}</h3>
  </div>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`rounded-xl border px-4 py-2.5 font-body text-sm font-semibold transition-colors duration-200 ${active ? "border-[#00A8E0] bg-[#00A8E0]/10 text-[#0057B8]" : "border-black/15 bg-white text-[#3c4650] hover:border-[#00A8E0]/60"}`}
  >
    {children}
  </button>
);

const AddonCard = ({ d, active, onToggle }) => (
  <button
    type="button"
    data-testid={`att-addon-${d.id}`}
    onClick={onToggle}
    className={`text-left rounded-2xl border p-4 bg-white transition-all duration-200 flex items-center gap-4 ${active ? "border-[#00A8E0] shadow-md" : "border-black/10 hover:border-[#00A8E0]/50"}`}
  >
    <div className="h-16 w-16 shrink-0 flex items-center justify-center bg-[#f7f9fa] rounded-xl">
      <img src={d.img} alt={d.name} className="max-h-14 max-w-full w-auto object-contain" loading="lazy" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="font-body text-sm font-bold text-[#1d2329] leading-snug">{d.name}</p>
      <p className="font-body text-xs text-[#6b7683]">{d.monthly} for 36 mo.</p>
    </div>
    <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${active ? "border-[#00A8E0] text-white" : "border-black/20 text-transparent"}`} style={active ? { backgroundColor: ATT_BLUE } : {}}>
      <Check size={13} />
    </span>
  </button>
);

export default function ATTDeviceDetailPage() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const device = findDevice(deviceId);

  const [color, setColor] = useState(device ? device.colors[0][0] : "");
  const [storageIdx, setStorageIdx] = useState(0);
  const [payment, setPayment] = useState("installments");
  const [tradeIn, setTradeIn] = useState("");
  const [promoTier, setPromoTier] = useState("");
  const [nextUp, setNextUp] = useState("");
  const [plan, setPlan] = useState("");
  const [addons, setAddons] = useState({});
  const [insurance, setInsurance] = useState("");

  const addonPool = useMemo(
    () => [...ATT_CATALOG.tablets.map((d) => ({ ...d, category: "Tablet" })), ...ATT_CATALOG.watches.map((d) => ({ ...d, category: "Watch" }))].filter((d) => d.id !== deviceId),
    [deviceId]
  );

  if (!device) return <Navigate to="/att/devices" replace />;

  const price = storagePrice(device, storageIdx);
  const deviceMonthly = price / 36;
  const colorIdx = device.colors.findIndex(([n]) => n === color);
  const deviceImg =
    (device.colorImgs && device.colorImgs[color]) ||
    (colorIdx > 0 ? `/att/recolor/${device.id}-${color.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png` : device.img);
  const tierObj = tradeIn === "Yes" && payment === "installments" ? ATT_TRADE_PROMO_TIERS.find((t) => t.id === promoTier) : null;
  const promoCredit = tierObj ? Math.min(deviceMonthly, tierObj.credit / 36) : 0;
  const nextUpMonthly = payment === "installments" && nextUp === "Yes" ? ATT_NEXT_UP.price : 0;
  const planObj = ATT_PLAN_DETAILS.residential.find((p) => p.name === plan);
  const planMonthly = planObj ? parseMoney(planObj.price) : 0;
  const selectedAddons = addonPool.filter((d) => addons[d.id]);
  const addonsMonthly = selectedAddons.reduce((s, d) => s + parseMoney(d.monthly), 0);
  const insObj = ATT_INSURANCE.find((i) => i.id === insurance);
  const insMonthly = insObj ? insObj.price : 0;

  const monthlyTotal = (payment === "installments" ? deviceMonthly - promoCredit : 0) + planMonthly + addonsMonthly + insMonthly + nextUpMonthly;
  const oneTime = payment === "retail" ? price : 0;
  const storageLabel = device.storage[storageIdx];

  const continueToPreorder = () => {
    const lines = [
      `${device.name} — ${color}, ${storageLabel}`,
      payment === "installments" ? `Payment: 36-mo installments (${fmt(deviceMonthly)}/mo, 0% APR, $0 down)` : `Payment: Pay in full (${fmt(price)})`,
      `Trade-in: ${tradeIn || "Not answered"}`,
      ...(tierObj ? [`Trade-in promo: ${tierObj.label}${promoCredit > 0 ? ` (est. −${fmt(promoCredit)}/mo credit)` : ""}`] : []),
      ...(payment === "installments" ? [`AT&T Next Up Anytime: ${nextUp || "Not answered"}${nextUp === "Yes" ? ` (+${fmt(ATT_NEXT_UP.price)}/mo)` : ""}`] : []),
      `Wireless plan: ${plan || "Choose with us later"}${planObj ? ` (${fmt(planMonthly)}/mo per line)` : ""}`,
      `Add-ons: ${selectedAddons.length ? selectedAddons.map((d) => `${d.name} (${d.monthly})`).join(", ") : "None"}`,
      `Insurance: ${insObj ? insObj.name + (insObj.price ? ` (${fmt(insObj.price)}/mo)` : "") : "Not answered"}`,
      `Estimated monthly: ${fmt(monthlyTotal)}/mo · Estimated one-time: ${fmt(oneTime)} + taxes/fees`,
    ];
    const cart = {
      device: { id: device.id, name: device.name, category: device.category, img: device.img },
      color,
      storage: storageLabel,
      payment,
      tradeIn,
      plan,
      addons: selectedAddons.map((d) => ({ name: d.name, category: d.category })),
      insurance: insObj ? insObj.name : "",
      estMonthly: fmt(monthlyTotal) + "/mo",
      estOnetime: fmt(oneTime),
      summary: lines.join("\n"),
    };
    sessionStorage.setItem("att_cart", JSON.stringify(cart));
    navigate("/att/preorder");
  };

  return (
    <div className="py-10 md:py-16" data-testid="att-device-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link to="/att/devices" data-testid="att-back-to-devices" className="inline-flex items-center gap-2 font-body text-sm font-semibold text-[#3c4650] hover:text-[#00A8E0] transition-colors">
          <ArrowLeft size={15} /> All devices
        </Link>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[400px,1fr] gap-10">
          {/* Left: image + live summary */}
          <div className="lg:sticky lg:top-40 self-start space-y-5">
            <div className="rounded-3xl border border-black/10 bg-white p-8 shadow-sm">
              <div className="h-72 flex items-center justify-center">
                <img src={deviceImg} alt={`${device.name} in ${color}`} className="max-h-64 max-w-full w-auto object-contain transition-opacity duration-300" data-testid="att-detail-device-image" onError={(e) => { e.currentTarget.src = device.img; }} />
              </div>
              <p className="mt-4 font-body text-[11px] uppercase tracking-widest text-[#6b7683]">{device.brand} · {device.category}</p>
              <h1 className="font-serif-display text-2xl md:text-3xl font-bold text-[#1d2329]">{device.name}</h1>
              <p className="mt-1 font-body text-xs font-semibold" style={{ color: ATT_BLUE }}>{device.promo}</p>
            </div>

            <div className="rounded-3xl border border-black/10 bg-[#06111f] p-6 text-white" data-testid="att-order-summary">
              <p className="font-body text-xs tracking-[0.25em] uppercase text-white/50">Your build</p>
              <ul className="mt-4 space-y-2 font-body text-sm text-white/80">
                <li data-testid="att-summary-device">{device.name} · {color} · {storageLabel}</li>
                <li data-testid="att-summary-payment">{payment === "installments" ? `${fmt(deviceMonthly)}/mo × 36 (0% APR)` : `${fmt(price)} paid in full`}</li>
                {tradeIn && <li>Trade-in: {tradeIn}</li>}
                {promoCredit > 0 && <li className="text-emerald-400" data-testid="att-summary-promo">Promo credit est. −{fmt(promoCredit)}/mo</li>}
                {nextUpMonthly > 0 && <li>+ {ATT_NEXT_UP.name} · {fmt(ATT_NEXT_UP.price)}/mo</li>}
                {plan && <li>{plan} · {fmt(planMonthly)}/mo per line</li>}
                {selectedAddons.map((d) => <li key={d.id}>+ {d.name} · {d.monthly}</li>)}
                {insObj && insObj.price > 0 && <li>{insObj.name} · {fmt(insObj.price)}/mo</li>}
              </ul>
              <div className="mt-5 border-t border-white/10 pt-4 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-body text-sm text-white/60">Est. monthly</span>
                  <span className="font-serif-display text-2xl font-bold" style={{ color: ATT_BLUE }} data-testid="att-summary-monthly">{fmt(monthlyTotal)}/mo</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-body text-sm text-white/60">Est. one-time</span>
                  <span className="font-body text-sm font-bold text-white" data-testid="att-summary-onetime">{fmt(oneTime)}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={continueToPreorder}
                data-testid="att-continue-preorder"
                className="btn-sweep mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-body text-sm font-semibold text-white"
                style={{ backgroundColor: ATT_BLUE }}
              >
                Continue to Pre-Order <ArrowRight size={15} />
              </button>
              <p className="mt-3 font-body text-[10px] leading-relaxed text-white/40">
                Estimates only — plus taxes &amp; fees. Trade-in credits and promos confirmed by AT&amp;T at time of order. No payment is taken on this site.
              </p>
            </div>
          </div>

          {/* Right: configuration steps */}
          <div className="space-y-10">
            <section>
              <StepLabel n={1}>Pick your color</StepLabel>
              <div className="mt-4 flex flex-wrap gap-3">
                {device.colors.map(([n, h]) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`att-color-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setColor(n)}
                    className={`flex items-center gap-2.5 rounded-full border px-4 py-2 font-body text-sm font-semibold transition-colors duration-200 ${color === n ? "border-[#00A8E0] bg-[#00A8E0]/10 text-[#0057B8]" : "border-black/15 bg-white text-[#3c4650] hover:border-[#00A8E0]/60"}`}
                  >
                    <span className="h-5 w-5 rounded-full border border-black/20" style={{ backgroundColor: h }} />
                    {n}
                  </button>
                ))}
              </div>
            </section>

            <section>
              <StepLabel n={2}>{/GB|TB/.test(device.storage[0]) ? "Choose your storage" : "Choose your size"}</StepLabel>
              <div className="mt-4 flex flex-wrap gap-3">
                {device.storage.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`att-storage-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setStorageIdx(i)}
                    className={`rounded-xl border px-5 py-3 text-left transition-colors duration-200 ${storageIdx === i ? "border-[#00A8E0] bg-[#00A8E0]/10" : "border-black/15 bg-white hover:border-[#00A8E0]/60"}`}
                  >
                    <p className={`font-body text-sm font-bold ${storageIdx === i ? "text-[#0057B8]" : "text-[#1d2329]"}`}>{s}</p>
                    <p className="font-body text-xs text-[#6b7683]">{fmt(storagePrice(device, i))} · {fmt(storagePrice(device, i) / 36)}/mo</p>
                  </button>
                ))}
              </div>
            </section>

            <section>
              <StepLabel n={3}>How do you want to pay?</StepLabel>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  data-testid="att-payment-installments"
                  onClick={() => setPayment("installments")}
                  className={`rounded-2xl border p-5 text-left bg-white transition-all duration-200 ${payment === "installments" ? "border-[#00A8E0] shadow-md" : "border-black/10 hover:border-[#00A8E0]/50"}`}
                >
                  <p className="font-body text-sm font-bold text-[#1d2329]">36 monthly installments</p>
                  <p className="mt-1 font-serif-display text-2xl font-bold" style={{ color: ATT_BLUE }}>{fmt(deviceMonthly)}/mo</p>
                  <p className="mt-1 font-body text-xs text-[#6b7683]">0% APR · $0 down for well-qualified customers · tax due at sale</p>
                </button>
                <button
                  type="button"
                  data-testid="att-payment-retail"
                  onClick={() => setPayment("retail")}
                  className={`rounded-2xl border p-5 text-left bg-white transition-all duration-200 ${payment === "retail" ? "border-[#00A8E0] shadow-md" : "border-black/10 hover:border-[#00A8E0]/50"}`}
                >
                  <p className="font-body text-sm font-bold text-[#1d2329]">Pay in full</p>
                  <p className="mt-1 font-serif-display text-2xl font-bold text-[#1d2329]">{fmt(price)}</p>
                  <p className="mt-1 font-body text-xs text-[#6b7683]">One-time retail price · no installment agreement</p>
                </button>
              </div>
              {payment === "installments" && (
                <div className="mt-4 rounded-2xl border border-black/10 bg-white p-5" data-testid="att-nextup">
                  <p className="font-body text-sm font-bold text-[#1d2329]">Are you an AT&amp;T Next Up customer — or want to upgrade anytime?</p>
                  <p className="mt-1.5 font-body text-xs leading-relaxed text-[#6b7683]">{ATT_NEXT_UP.desc}</p>
                  <div className="mt-3 flex gap-3">
                    <Chip active={nextUp === "Yes"} onClick={() => setNextUp("Yes")} testid="att-nextup-yes">Yes — add Next Up (+{fmt(ATT_NEXT_UP.price)}/mo)</Chip>
                    <Chip active={nextUp === "No"} onClick={() => setNextUp("No")} testid="att-nextup-no">No thanks</Chip>
                  </div>
                </div>
              )}
            </section>

            <section>
              <StepLabel n={4}>Are you trading in a device?</StepLabel>
              <div className="mt-4 flex gap-3">
                <Chip active={tradeIn === "Yes"} onClick={() => setTradeIn("Yes")} testid="att-detail-tradein-yes">Yes</Chip>
                <Chip active={tradeIn === "No"} onClick={() => setTradeIn("No")} testid="att-detail-tradein-no">No</Chip>
              </div>
              {tradeIn === "Yes" && (
                <div className="mt-4 rounded-2xl border border-[#00A8E0]/40 bg-[#00A8E0]/5 p-5" data-testid="att-detail-tradein-note">
                  <p className="font-body text-sm font-semibold" style={{ color: "#0057B8" }}>{device.promo}</p>
                  <p className="mt-2 font-body text-xs leading-relaxed text-[#3c4650]">
                    Great — you'll enter your trade-in details (model, storage, condition, IMEI) on the pre-order form.
                    Final trade-in credit is confirmed by AT&amp;T after inspection and applied as monthly bill credits over 36 months.
                  </p>
                  {payment === "installments" && (
                    <div className="mt-4" data-testid="att-promo-tiers">
                      <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#3c4650]">Promo pricing — how much is your trade-in worth?</p>
                      <div className="mt-3 space-y-2.5">
                        {ATT_TRADE_PROMO_TIERS.map((t) => {
                          const credit = Math.min(deviceMonthly, t.credit / 36);
                          return (
                            <button
                              key={t.id}
                              type="button"
                              data-testid={`att-promo-tier-${t.id}`}
                              onClick={() => setPromoTier(promoTier === t.id ? "" : t.id)}
                              className={`w-full rounded-xl border p-4 text-left bg-white transition-all duration-200 flex items-start justify-between gap-3 ${promoTier === t.id ? "border-[#00A8E0] shadow-md" : "border-black/10 hover:border-[#00A8E0]/50"}`}
                            >
                              <div>
                                <p className="font-body text-sm font-bold text-[#1d2329]">{t.label}</p>
                                <p className="mt-0.5 font-body text-xs text-[#6b7683] leading-relaxed">{t.desc}</p>
                              </div>
                              <span className="shrink-0 font-body text-sm font-bold whitespace-nowrap" style={{ color: t.credit ? "#059669" : "#0057B8" }}>
                                {t.credit ? `−${fmt(credit)}/mo` : "We'll quote it"}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <p className="mt-2 font-body text-[11px] leading-relaxed text-[#6b7683]">
                        Estimated promo pricing only — your exact credit depends on your phone's model and condition, confirmed by AT&amp;T at time of order.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section>
              <StepLabel n={5}>Pick your wireless plan</StepLabel>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {ATT_PLAN_DETAILS.residential.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    data-testid={`att-plan-pick-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setPlan(plan === p.name ? "" : p.name)}
                    className={`rounded-2xl border p-5 text-left bg-white transition-all duration-200 ${plan === p.name ? "border-[#00A8E0] shadow-md" : "border-black/10 hover:border-[#00A8E0]/50"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-body text-sm font-bold text-[#1d2329]">{p.name}</p>
                      {plan === p.name && <Check size={16} style={{ color: ATT_BLUE }} />}
                    </div>
                    <p className="mt-1 font-serif-display text-xl font-bold" style={{ color: ATT_BLUE }}>{p.price}</p>
                    <p className="font-body text-[11px] text-[#6b7683]">{p.per}</p>
                  </button>
                ))}
              </div>
              <p className="mt-3 font-body text-xs text-[#6b7683]">Not sure? Leave it unselected — we'll help you choose when we call. Multi-line and hero (teacher/military/police/doctor/nurse) discounts get applied on your order.</p>
            </section>

            <section>
              <StepLabel n={6}>Add a tablet, watch — or both</StepLabel>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {addonPool.map((d) => (
                  <AddonCard key={d.id} d={d} active={!!addons[d.id]} onToggle={() => setAddons((a) => ({ ...a, [d.id]: !a[d.id] }))} />
                ))}
              </div>
            </section>

            <section>
              <StepLabel n={7}>Protect it with insurance</StepLabel>
              <div className="mt-4 space-y-3">
                {ATT_INSURANCE.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    data-testid={`att-insurance-${i.id}`}
                    onClick={() => setInsurance(i.id)}
                    className={`w-full rounded-2xl border p-5 text-left bg-white transition-all duration-200 flex items-start gap-4 ${insurance === i.id ? "border-[#00A8E0] shadow-md" : "border-black/10 hover:border-[#00A8E0]/50"}`}
                  >
                    <ShieldCheck size={22} className="mt-0.5 shrink-0" style={{ color: insurance === i.id ? ATT_BLUE : "#9aa4af" }} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-body text-sm font-bold text-[#1d2329]">{i.name}</p>
                        <p className="font-body text-sm font-bold whitespace-nowrap" style={{ color: ATT_BLUE }}>{i.price ? `${fmt(i.price)}/mo` : "$0"}</p>
                      </div>
                      <p className="mt-1 font-body text-xs leading-relaxed text-[#6b7683]">{i.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </section>

            <button
              type="button"
              onClick={continueToPreorder}
              data-testid="att-continue-preorder-bottom"
              className="btn-sweep w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-body text-sm font-semibold text-white"
              style={{ backgroundColor: ATT_BLUE }}
            >
              Continue to Pre-Order — {fmt(monthlyTotal)}/mo est. <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
